import { useEffect, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { useCompleteReconciliation, useReconciliation } from '../../hooks/useReconciliation';
import { ApiError, fetchBlobObjectUrl } from '../../lib/api';

/**
 * Dedicated full-page review-and-complete view for a reconciliation.
 * Replaced the old ReportPreviewModal so the PDF gets a full page and the
 * browser back button returns to the worksheet.
 *
 * The worksheet page navigates here when the user clicks "Review & complete".
 * The Notes input lives here (it's only relevant at completion time), so we
 * don't have to round-trip notes through router state.
 */
export const Route = createFileRoute('/_auth/reconcile/$id/preview')({
  component: ReconciliationPreviewPage,
});

function apiErrorMessage(err: unknown): string | undefined {
  if (!err) return undefined;
  if (err instanceof ApiError) {
    return (err.body as { error?: string } | null)?.error ?? err.message;
  }
  return (err as Error).message ?? 'Request failed';
}

function ReconciliationPreviewPage() {
  const { id: reconId } = Route.useParams();
  const companyId = useActiveCompanyId();
  const navigate = useNavigate();
  const reconQuery = useReconciliation(companyId, reconId);
  const complete = useCompleteReconciliation(companyId);
  const [notes, setNotes] = useState('');
  const [url, setUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!companyId) return;
    let objectUrl: string | null = null;
    let cancelled = false;
    fetchBlobObjectUrl(`/companies/${companyId}/reconciliations/${reconId}/report.pdf`)
      .then((u) => {
        if (cancelled) {
          URL.revokeObjectURL(u);
          return;
        }
        objectUrl = u;
        setUrl(u);
      })
      .catch((e) => setLoadError(e instanceof Error ? e.message : 'Could not load preview'));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [companyId, reconId]);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <p className="muted">No active company.</p>
      </div>
    );
  }

  const recon = reconQuery.data;
  const completeError = apiErrorMessage(complete.error);
  // After completion, the recon's status flips to 'completed' — bounce back to the list.
  const alreadyComplete = recon?.status === 'completed';

  return (
    <div style={{ maxWidth: 880, margin: '0 auto' }}>
      <Link to="/reconcile/$id" params={{ id: reconId }} style={{ fontSize: 14 }}>
        ← Back to worksheet
      </Link>
      <h1 style={{ fontSize: 22, margin: '8px 0 8px' }}>
        Reconciliation report preview
      </h1>
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
        Review the balance proof, cleared items, and outstanding checks / deposits in transit.
        Completing locks the cleared entries and saves this report as a companion document.
      </p>

      {alreadyComplete && (
        <div className="card" style={{ marginBottom: 16, padding: 12, background: 'var(--bg-surface)' }}>
          <strong>This reconciliation is already complete.</strong>{' '}
          <Link to="/reconcile">Return to the reconciliations list</Link>.
        </div>
      )}

      <div
        style={{
          minHeight: 480,
          border: '1px solid var(--border, #dfe4ec)',
          borderRadius: 6,
          overflow: 'hidden',
          background: '#f6f7f9',
          marginBottom: 16,
        }}
      >
        {loadError ? (
          <div style={{ padding: 24 }} className="field-error">
            {loadError}
          </div>
        ) : url ? (
          <iframe
            title="Reconciliation report"
            src={url}
            style={{ width: '100%', height: '70vh', border: 0 }}
          />
        ) : (
          <p className="muted" style={{ padding: 24 }}>
            Generating preview…
          </p>
        )}
      </div>

      {!alreadyComplete && (
        <div className="card" style={{ padding: 16 }}>
          <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 4 }}>
            Notes (optional)
          </label>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Anything worth flagging on this reconciliation…"
            style={{ width: '100%', resize: 'vertical' }}
            disabled={complete.isPending}
          />

          {completeError && (
            <div className="field-error" style={{ marginTop: 12 }}>
              {completeError}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => void navigate({ to: '/reconcile/$id', params: { id: reconId } })}
              disabled={complete.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={complete.isPending}
              onClick={() =>
                complete.mutate(
                  { id: reconId, input: notes.trim() ? { notes: notes.trim() } : {} },
                  {
                    onSuccess: () => void navigate({ to: '/reconcile' }),
                  },
                )
              }
            >
              {complete.isPending ? 'Completing…' : 'Complete reconciliation'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
